'use client';

/**
 * Agent Debug Feeds — open/close + callId context.
 * The provider is mounted globally (in app/template.tsx) so the user-menu
 * item and the floating dialog share state; the active-call page binds the
 * current callId via <AgentDebugCallIdBinder>. Opened from the user menu
 * (admins only) or Ctrl/Cmd+Shift+A. See docs/AGENT_DEBUG_FEEDS_DESIGN.md §5.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface AgentDebugContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  callId: string | null;
  setCallId: (id: string | null) => void;
}

const AgentDebugContext = createContext<AgentDebugContextValue | null>(null);

export function AgentDebugProvider({
  children,
  callId: initialCallId = null,
}: {
  children: React.ReactNode;
  callId?: string | null;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [callId, setCallId] = useState<string | null>(initialCallId);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  const value = useMemo<AgentDebugContextValue>(
    () => ({ isOpen, open, close, toggle, callId, setCallId }),
    [isOpen, open, close, toggle, callId],
  );

  return <AgentDebugContext.Provider value={value}>{children}</AgentDebugContext.Provider>;
}

/** Returns the context, or a no-op fallback when used outside a provider (so
 * the user-menu item doesn't crash on pages that don't mount the dialog). */
export function useAgentDebug(): AgentDebugContextValue {
  const ctx = useContext(AgentDebugContext);
  if (ctx) return ctx;
  return {
    isOpen: false,
    open: () => {},
    close: () => {},
    toggle: () => {},
    callId: null,
    setCallId: () => {},
  };
}

/** True only when an AgentDebugProvider is mounted above. */
export function useHasAgentDebug(): boolean {
  return useContext(AgentDebugContext) !== null;
}

/**
 * Mount this (rendering `null`) inside the active-call page to bind the
 * current callId into the global AgentDebug context — that's what makes the
 * "Agent Tools & Thoughts" user-menu item appear and gives the dialog a call
 * to inspect. Clears the binding on unmount.
 */
export function AgentDebugCallIdBinder({ callId }: { callId: string | null | undefined }) {
  const { setCallId } = useAgentDebug();
  useEffect(() => {
    setCallId(callId ?? null);
    return () => setCallId(null);
  }, [callId, setCallId]);
  return null;
}
